import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb, { getArticleBreadcrumb } from "@/components/base/Breadcrumb";
import { getArticleById } from "@/mocks/articles";
import { categorySlugMap } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const article = getArticleById(slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <SEOHead title="文章未找到" description="抱歉，您要查看的文章不存在。" keywords="云体育" noindex />
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除。</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const catName = categorySlugMap[article.category] || article.category;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "云体育",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://yuntrain.github.io",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: catName,
        item: `https://yuntrain.github.io/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://yuntrain.github.io/article/${article.id}`}
        ogImage={article.coverImage}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <main className="min-h-screen bg-background-50">
        <article>
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-6">
            <Breadcrumb
              items={getArticleBreadcrumb(article.category, article.title)}
            />
          </div>

          <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 云体育`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 text-white/70 text-xs mb-4 flex-wrap">
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
                <span>·</span>
                <span>{article.author}</span>
                <span>·</span>
                <Link
                  to={`/category/${article.category}`}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {catName}
                </Link>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:mb-5 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回{catName}
              </Link>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}